import {
  CAMERA_3D_FOV,
  DRAW_FIT_COTA_MARGIN_MM,
  DRAW_FIT_EFFECTIVE_FOV,
  DRAW_FIT_MIN_HEIGHT_ABOVE_BBOX,
  DRAW_FIT_MIN_POLAR_FOR_ORBIT,
  DRAW_FIT_TOP_DOWN_PADDING,
} from '@constants'
import type CameraControls from 'camera-controls'
import { PerspectiveCamera, Quaternion, Spherical, Vector3 } from 'three'

import type { DrawFitFootprint } from './fitCameraToItems'
import type { CameraView } from './cameraView'

const _localRight = new Vector3()
const _rightOnGround = new Vector3()
const _cotaCenterBiasLocal = new Vector3()
const _probeQuat = new Quaternion()

type CameraControlsWithOrbit = CameraControls & {
  setLookAt?: (
    px: number,
    py: number,
    pz: number,
    tx: number,
    ty: number,
    tz: number,
    enableTransition: boolean,
  ) => Promise<void> | void
  rotateTo?: (azimuthAngle: number, polarAngle: number, enableTransition: boolean) => Promise<void>
  dollyTo?: (distance: number, enableTransition: boolean) => Promise<void>
  _spherical?: Spherical
  _sphericalEnd?: Spherical
  normalizeRotations?: () => void
}

const _offset = new Vector3()
const _syncSpherical = new Spherical()

const _probeCamera = new PerspectiveCamera()

/** `up` del encuadre cenital; se usa una sola vez al pasar a órbita Y-up. */
let activeDrawFitCameraUp: Vector3 | null = null
let drawFitOrbitSyncPending = false

export function getActiveDrawFitCameraUp(): Vector3 | null {
  return activeDrawFitCameraUp
}

export function clearActiveDrawFitCameraUp(): void {
  activeDrawFitCameraUp = null
  drawFitOrbitSyncPending = false
}

export function markDrawFitOrbitSyncPending(): void {
  drawFitOrbitSyncPending = true
}

/** Posiciona una cámara Y-up en coordenadas esféricas (convención camera-controls / Three.js). */
function setCameraOnSphericalYUp(
  cam: PerspectiveCamera,
  target: Vector3,
  distance: number,
  azimuth: number,
  polar: number,
): void {
  const sinPolar = Math.sin(polar)
  const cosPolar = Math.cos(polar)
  cam.up.set(0, 1, 0)
  cam.position.set(
    target.x + distance * sinPolar * Math.sin(azimuth),
    target.y + distance * cosPolar,
    target.z + distance * sinPolar * Math.cos(azimuth),
  )
  cam.lookAt(target)
  cam.updateMatrixWorld()
}

/**
 * Busca azimut/polar Y-up cuyo quaternion coincide con la vista cenital (up custom).
 * camera-controls no guarda roll; hay que codificarlo en el azimut con polar > 0.
 */
function resolveYUpOrbitMatchingDrawView(
  camera: PerspectiveCamera,
  target: Vector3,
  distance: number,
): { azimuth: number; polar: number } {
  const desiredQuat = camera.quaternion.clone()
  const polarCandidates = [
    DRAW_FIT_MIN_POLAR_FOR_ORBIT,
    DRAW_FIT_MIN_POLAR_FOR_ORBIT + 0.01,
    DRAW_FIT_MIN_POLAR_FOR_ORBIT + 0.02,
  ]

  let bestAzimuth = 0
  let bestPolar = polarCandidates[0]
  let bestError = Infinity

  for (const polar of polarCandidates) {
    const safePolar = Math.max(polar, 1e-4)
    for (let deg = 0; deg < 360; deg += 1) {
      const azimuth = (deg * Math.PI) / 180
      setCameraOnSphericalYUp(_probeCamera, target, distance, azimuth, safePolar)
      _probeQuat.copy(_probeCamera.quaternion)
      const error = 1 - Math.abs(desiredQuat.dot(_probeQuat))
      if (error < bestError) {
        bestError = error
        bestAzimuth = azimuth
        bestPolar = safePolar
      }
    }
  }

  return {
    azimuth: bestAzimuth,
    polar: bestPolar,
  }
}

/** Alinea `_spherical` interno con la posición actual (convención camera-controls). */
function syncControlsSphericalFromCamera(
  controls: CameraControls,
  camera: PerspectiveCamera,
  target: Vector3,
): void {
  const cc = controls as CameraControlsWithOrbit
  _offset.copy(camera.position).sub(target)
  _syncSpherical.setFromVector3(_offset)

  if (cc._spherical) {
    cc._spherical.copy(_syncSpherical)
  }
  if (cc._sphericalEnd) {
    cc._sphericalEnd.copy(_syncSpherical)
  }
  cc.normalizeRotations?.()
}

/** Desplaza el target para compensar cotas en +X/+Z local y centrar el módulo. */
function resolveDrawFitTargetCenter(footprint: DrawFitFootprint): Vector3 {
  _cotaCenterBiasLocal.set(-DRAW_FIT_COTA_MARGIN_MM * 0.22, 0, -DRAW_FIT_COTA_MARGIN_MM * 0.15)
  return footprint.center
    .clone()
    .add(_cotaCenterBiasLocal.applyQuaternion(footprint.anchorQuaternion))
}

/**
 * Define el `up` de la cámara para vista cenital sin rotar el módulo:
 * el eje local +X del ancla sigue apuntando a la derecha de pantalla y +Z hacia abajo.
 */
export function resolveTopDownCameraUp(
  camera: PerspectiveCamera,
  anchorQuaternion: Quaternion,
): Vector3 {
  _localRight.set(1, 0, 0).applyQuaternion(anchorQuaternion)
  _rightOnGround.set(_localRight.x, 0, _localRight.z)

  if (_rightOnGround.lengthSq() < 1e-6) {
    camera.up.set(0, 0, 1)
    return camera.up.clone()
  }

  _rightOnGround.normalize()
  camera.up.set(_rightOnGround.z, 0, -_rightOnGround.x).normalize()
  return camera.up.clone()
}

export function restoreDefaultCameraUp(camera: PerspectiveCamera): void {
  camera.up.set(0, 1, 0)
}

/**
 * Pasa de encuadre cenital (up custom) a órbita Y-up estándar sin voltear la escena.
 */
export function transitionDrawFitToStandardOrbit(
  controls: CameraControls,
  camera: PerspectiveCamera,
): boolean {
  const drawUp = activeDrawFitCameraUp
  if (!drawUp) return false

  camera.updateMatrixWorld()
  const target = controls.getTarget(new Vector3())
  const distance = Math.max(camera.position.distanceTo(target), 1)

  const { azimuth, polar } = resolveYUpOrbitMatchingDrawView(camera, target, distance)

  restoreDefaultCameraUp(camera)
  controls.setTarget(target.x, target.y, target.z, false)

  setCameraOnSphericalYUp(camera, target, distance, azimuth, polar)
  syncControlsSphericalFromCamera(controls, camera, target)
  controls.update()

  clearActiveDrawFitCameraUp()
  return true
}

export function syncDrawCameraOrbitIfPending(
  controls: CameraControls,
  camera: PerspectiveCamera,
): boolean {
  if (!drawFitOrbitSyncPending || !activeDrawFitCameraUp) return false

  const synced = transitionDrawFitToStandardOrbit(controls, camera)
  drawFitOrbitSyncPending = false
  return synced
}

/** Altura de la cámara para encuadrar la huella (vista vertical, sin inclinación). */
export function computeDrawTopDownCameraHeight(
  footprintWidth: number,
  footprintDepth: number,
  maxY: number,
  fovDeg: number,
  aspect: number,
): number {
  const framingFov = DRAW_FIT_EFFECTIVE_FOV > 0 ? DRAW_FIT_EFFECTIVE_FOV : fovDeg
  const safeAspect = aspect > 0 ? aspect : 1
  const vFovRad = (framingFov * Math.PI) / 180
  const hFovRad = 2 * Math.atan(Math.tan(vFovRad / 2) * safeAspect)

  const framedWidth = Math.max(footprintWidth, 1) + DRAW_FIT_COTA_MARGIN_MM
  const framedDepth = Math.max(footprintDepth, 1) + DRAW_FIT_COTA_MARGIN_MM

  const halfWidth = framedWidth / 2
  const halfDepth = framedDepth / 2

  const distanceForWidth = halfWidth / Math.tan(hFovRad / 2)
  const distanceForDepth = halfDepth / Math.tan(vFovRad / 2)
  const verticalDistance = Math.max(distanceForWidth, distanceForDepth) * DRAW_FIT_TOP_DOWN_PADDING

  return maxY + Math.max(verticalDistance, DRAW_FIT_MIN_HEIGHT_ABOVE_BBOX)
}

export function buildDrawTopDownCameraView(
  footprint: DrawFitFootprint,
  camera: PerspectiveCamera,
): CameraView {
  const fov = camera.fov > 0 ? camera.fov : CAMERA_3D_FOV
  const aspect = camera.aspect > 0 ? camera.aspect : 1
  const target = resolveDrawFitTargetCenter(footprint)
  const cameraY = computeDrawTopDownCameraHeight(
    footprint.footprintWidth,
    footprint.footprintDepth,
    footprint.maxY,
    fov,
    aspect,
  )

  return {
    position: new Vector3(target.x, cameraY, target.z),
    target,
  }
}

export function applyDrawTopDownCameraView(
  controls: CameraControls,
  camera: PerspectiveCamera,
  view: CameraView,
  anchorQuaternion: Quaternion,
): void {
  const drawUp = resolveTopDownCameraUp(camera, anchorQuaternion)
  activeDrawFitCameraUp = drawUp.clone()

  const verticalDistance = Math.max(view.position.y - view.target.y, 1)
  const position = new Vector3(view.target.x, view.target.y + verticalDistance, view.target.z)

  const setLookAt = (controls as CameraControlsWithOrbit).setLookAt
  if (typeof setLookAt === 'function') {
    void setLookAt.call(
      controls,
      position.x,
      position.y,
      position.z,
      view.target.x,
      view.target.y,
      view.target.z,
      false,
    )
  } else {
    controls.setTarget(view.target.x, view.target.y, view.target.z, false)
    controls.setPosition(position.x, position.y, position.z, false)
  }

  camera.up.copy(drawUp)
  controls.update()
}
